import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { IMG } from "@/data/content";
import { ArrowRight, Search, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
};

const Blog = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const [qInput, setQInput] = useState(q);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (category) p.set("category", category);
        if (q) p.set("q", q);
        const [list, facets] = await Promise.all([
          axios.get(`${API}/blog?${p.toString()}`),
          axios.get(`${API}/blog/facets`),
        ]);
        setItems(list.data.items || []);
        setCats(facets.data.categories || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    })();
  }, [category, q]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("category", c); else next.delete("category");
    setParams(next);
  };
  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (qInput) next.set("q", qInput); else next.delete("q");
    setParams(next);
  };

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="Journal"
        title={<>Letters from the <em>practice</em>.</>}
        subtitle="Essays on geomancy, manifestation, the seeker's path — written from the studio."
        image={IMG.ancientBook}
      />

      <section className="section">
        <div className="wrap grid gap-8 lg:grid-cols-12">
          <aside className="lg:col-span-3 order-2 lg:order-1 min-w-0">
            <form onSubmit={submitSearch} className="glass p-5 mb-6 overflow-hidden" data-testid="blog-search-form">
              <div className="eyebrow mb-3">Search</div>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="search"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Search essays…"
                  className="flex-1 min-w-0 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold"
                />
                <button type="submit" className="btn-gold px-3 py-2 shrink-0" data-testid="blog-search-submit">
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </form>
            <div className="glass p-5 overflow-hidden">
              <div className="eyebrow mb-3">Categories</div>
              <div className="grid gap-1.5">
                <button
                  type="button"
                  onClick={() => setCategory("")}
                  style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  className={`w-full block text-left text-sm italic font-display px-2 py-1.5 transition leading-snug ${!category ? "text-gold" : "text-ivory/70 hover:text-gold"}`}
                >
                  All
                </button>
                {cats.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                    className={`w-full block text-left text-sm italic font-display px-2 py-1.5 transition leading-snug ${category === c ? "text-gold" : "text-ivory/70 hover:text-gold"}`}
                    data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 order-1 lg:order-2 min-w-0">
            {loading && <div className="text-ivory/55">Loading essays…</div>}
            {!loading && items.length === 0 && (
              <div className="glass p-10 text-center text-ivory/60">
                No essays match your search. Try clearing the filters.
              </div>
            )}
            <div className="grid gap-8">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={i * 60}>
                  <article className="grid gap-6 lg:grid-cols-12 glass lift overflow-hidden">
                    <Link to={`/blog/${p.slug}`} className="lg:col-span-5 aspect-[16/10] lg:aspect-auto overflow-hidden">
                      <img
                        src={p.featured_image || IMG.ancientBook}
                        alt={p.title}
                        className="h-full w-full object-cover"
                      />
                    </Link>
                    <div className="lg:col-span-7 p-6 sm:p-8 flex flex-col justify-center min-w-0">
                      <div className="text-xs uppercase tracking-[0.25em] text-gold flex flex-wrap gap-2 items-center break-words">
                        {(p.categories || []).slice(0, 2).map((c) => (
                          <span key={c} style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>{c}</span>
                        ))}
                        <Calendar className="h-3 w-3" /> {formatDate(p.publish_at || p.created_at)}
                      </div>
                      <Link to={`/blog/${p.slug}`}>
                        <h2
                          style={{ overflowWrap: "anywhere", wordBreak: "break-word", hyphens: "auto" }}
                          className="mt-3 font-display text-2xl sm:text-3xl md:text-4xl leading-tight text-ivory hover:text-gold transition break-words"
                        >
                          {p.title}
                        </h2>
                      </Link>
                      {p.subtitle && <p className="mt-2 text-ivory/70 italic break-words">{p.subtitle}</p>}
                      <p className="mt-4 text-ivory/65 leading-relaxed break-words">{p.excerpt}</p>
                      <div className="mt-5 text-xs uppercase tracking-[0.25em] text-ivory/50 flex items-center justify-between">
                        <span>— {p.author || "The Studio"}</span>
                        <Link to={`/blog/${p.slug}`} className="text-gold inline-flex items-center gap-2">
                          Read <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
